/**
 * Pending Post-Test Service
 * Manages pending post-tests that students need to complete
 * Blocks session booking until post-test is completed
 *
 * ✅ TIMING & VISIBILITY RULES:
 * 1. Pending post-test is created IMMEDIATELY when session is marked as "completed" (via Cloud Function)
 * 2. reminderShown is set to TRUE by default (enables immediate visibility)
 * 3. PendingPostTestBanner appears on Find Tutors and My Sessions pages
 * 4. Banner shows until test is completed
 * 5. Banner checks against session endAt field (no 1-hour delay)
 */

import { db, auth } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";

export interface PendingPostTest {
  pendingPostTestId?: string;
  userId: string;
  userName: string;
  sessionId: string;
  sessionSubject: string;
  sessionDate: any; // Firestore Timestamp
  tutorId: string;
  tutorName: string;
  createdAt: any; // Firestore Timestamp
  deadline: any; // Firestore Timestamp - 24 hours after session end time
  isMandatory?: boolean; // True if deadline has passed
  reminderShown?: boolean; // ✅ Set to true by default - reminder modal shows once per login, banner shows on navigation
  lastReminderAt?: any; // Last reminder timestamp
}

class PendingPostTestService {
  private pendingPostTestsCollection = collection(db, "pending_post_tests");

  /**
   * Create a pending post-test record after session completion
   * Now supports multiple pending tests per user (stacking)
   */
  async createPendingPostTest(
    userId: string,
    userName: string,
    sessionId: string,
    sessionSubject: string,
    sessionDate: any, // This is the session START time
    tutorId: string,
    tutorName: string
  ): Promise<string> {
    try {
      logInfo("Creating pending post-test record", {
        userId: userId.substring(0, 8) + "***",
        sessionId,
        sessionSubject,
      });

      // Calculate deadline: 24 hours AFTER session END time (forgetting curve principle)
      // Standard tutoring session duration is 1 hour
      // So: deadline = session start time + 1 hour (session end) + 24 hours
      const sessionStartDate = sessionDate.toDate ? sessionDate.toDate() : new Date(sessionDate);

      // Get session end time (1 hour after start)
      const sessionEndTime = new Date(sessionStartDate);
      sessionEndTime.setHours(sessionEndTime.getHours() + 1);

      // Calculate deadline: 24 hours after session end
      const deadlineDate = new Date(sessionEndTime);
      deadlineDate.setHours(deadlineDate.getHours() + 24); // 24 hours countdown from session end
      const deadline = Timestamp.fromDate(deadlineDate);

      logInfo("Calculated post-test deadline", {
        sessionStart: sessionStartDate.toISOString(),
        sessionEnd: sessionEndTime.toISOString(),
        deadline: deadlineDate.toISOString(),
      });

      // Check if deadline has already passed
      const now = new Date();
      const isMandatory = now >= deadlineDate;

      const pendingPostTestData: Omit<PendingPostTest, "pendingPostTestId"> = {
        userId,
        userName,
        sessionId,
        sessionSubject,
        sessionDate,
        tutorId,
        tutorName,
        createdAt: Timestamp.now(),
        deadline,
        isMandatory,
        reminderShown: true, // ✅ Set to true immediately so reminder can appear right after session ends
      };

      // Use sessionId as document ID to allow multiple pending tests per user
      const docRef = doc(this.pendingPostTestsCollection, sessionId);
      await setDoc(docRef, pendingPostTestData);

      logSuccess("Pending post-test created with 24-hour deadline", {
        userId: userId.substring(0, 8) + "***",
        sessionId,
        deadline: deadlineDate.toISOString(),
        isMandatory,
      });

      return sessionId;
    } catch (error) {
      logError("Error creating pending post-test", error);
      throw error;
    }
  }

  /**
   * Check if user has any pending post-tests
   */
  async hasPendingPostTest(userId: string): Promise<boolean> {
    try {
      const q = query(this.pendingPostTestsCollection, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      logError("Error checking pending post-test", error);
      return false;
    }
  }

  /**
   * Get ALL pending post-tests for a user (ordered by deadline, mandatory first)
   * ✅ FIX: Only returns tests for sessions that have ALREADY OCCURRED (past sessions)
   */
  async getAllPendingPostTests(userId: string): Promise<PendingPostTest[]> {
    try {
      const q = query(this.pendingPostTestsCollection, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);

      const pendingTests: PendingPostTest[] = [];

      // ✅ CRITICAL FIX: Check each test against actual session completion status
      // Don't rely on time calculations - check if session is marked as completed
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data() as PendingPostTest;

        // ✅ CRITICAL: Use sessionId from data, not docSnapshot.id
        const sessionId = data.sessionId || docSnapshot.id;

        try {
          // Fetch the actual session document to check completion status
          const sessionDoc = await getDoc(doc(db, "sessions", sessionId));

          if (!sessionDoc.exists()) {
            logDebug("Session document not found, skipping", {
              sessionId: sessionId,
              pendingDocId: docSnapshot.id,
            });
            continue;
          }

          const sessionData = sessionDoc.data();

          // Session is complete if:
          // 1. status === "completed" OR
          // 2. completedAt timestamp exists
          const isCompleted = sessionData.status === "completed" || sessionData.completedAt != null;

          if (isCompleted) {
            pendingTests.push({
              pendingPostTestId: docSnapshot.id,
              ...data,
            } as PendingPostTest);

            logDebug("Including pending post-test for completed session", {
              sessionId: sessionId,
              status: sessionData.status,
              completedAt: sessionData.completedAt,
            });
          } else {
            logDebug("Skipping pending post-test for incomplete session", {
              sessionId: sessionId,
              status: sessionData.status,
              hasCompletedAt: !!sessionData.completedAt,
            });
          }
        } catch (error) {
          logDebug("Error checking session completion, skipping", {
            sessionId: sessionId,
            error,
          });
        }
      }

      // Sort: mandatory tests first, then by deadline (earliest first)
      pendingTests.sort((a, b) => {
        if (a.isMandatory && !b.isMandatory) return -1;
        if (!a.isMandatory && b.isMandatory) return 1;

        const aDeadline = a.deadline?.toMillis() || 0;
        const bDeadline = b.deadline?.toMillis() || 0;
        return aDeadline - bDeadline;
      });

      logInfo("Filtered pending post-tests (only completed sessions)", {
        userId: userId.substring(0, 8) + "***",
        totalInDb: querySnapshot.size,
        completedSessionsCount: pendingTests.length,
        incompleteSessionsSkipped: querySnapshot.size - pendingTests.length,
      });

      return pendingTests;
    } catch (error) {
      logError("Error getting all pending post-tests", error);
      return [];
    }
  }

  /**
   * Get count of pending post-tests for a user
   */
  async getPendingPostTestCount(userId: string): Promise<number> {
    try {
      const tests = await this.getAllPendingPostTests(userId);
      return tests.length;
    } catch (error) {
      logError("Error getting pending post-test count", error);
      return 0;
    }
  }

  /**
   * Get the next pending post-test (prioritizes mandatory, then earliest deadline)
   */
  async getNextPendingPostTest(userId: string): Promise<PendingPostTest | null> {
    try {
      const tests = await this.getAllPendingPostTests(userId);
      return tests.length > 0 ? tests[0] : null;
    } catch (error) {
      logError("Error getting next pending post-test", error);
      return null;
    }
  }

  /**
   * Get pending post-test details for a user (DEPRECATED - use getAllPendingPostTests)
   * @deprecated Use getAllPendingPostTests instead
   */
  async getPendingPostTest(userId: string): Promise<PendingPostTest | null> {
    return await this.getNextPendingPostTest(userId);
  }

  /**
   * Get pending post-test for current authenticated user
   */
  async getCurrentUserPendingPostTest(): Promise<PendingPostTest | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        logError("No authenticated user found");
        return null;
      }

      return await this.getNextPendingPostTest(currentUser.uid);
    } catch (error) {
      logError("Error getting current user pending post-test", error);
      return null;
    }
  }

  /**
   * Update reminder status for a specific session's pending test
   */
  async updateReminderShown(sessionId: string): Promise<void> {
    try {
      const docRef = doc(this.pendingPostTestsCollection, sessionId);
      await setDoc(
        docRef,
        {
          reminderShown: true,
          lastReminderAt: Timestamp.now(),
        },
        { merge: true }
      );

      logDebug("Reminder status updated", { sessionId });
    } catch (error) {
      logError("Error updating reminder status", error);
    }
  }

  /**
   * Update mandatory status for a specific pending test based on current time vs deadline
   * This method dynamically checks if the deadline has passed and updates the flag
   */
  async updateMandatoryStatus(sessionId: string): Promise<boolean> {
    try {
      const docRef = doc(this.pendingPostTestsCollection, sessionId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        logDebug("Pending test not found for mandatory status update", { sessionId });
        return false;
      }

      const data = docSnap.data() as PendingPostTest;
      const now = new Date();
      const deadlineDate = data.deadline.toDate();
      const isMandatory = now >= deadlineDate;

      // Update isMandatory flag if it changed
      if (isMandatory !== data.isMandatory) {
        await setDoc(docRef, { isMandatory }, { merge: true });
        logInfo("Updated mandatory status for pending test", {
          sessionId,
          isMandatory,
          deadline: deadlineDate.toISOString(),
          wasManadatory: data.isMandatory,
        });
      }

      return isMandatory;
    } catch (error) {
      logError("Error updating mandatory status", error);
      return false;
    }
  }

  /**
   * Update mandatory statuses for all pending tests of a user
   * Should be called on login, route changes, and periodic checks
   */
  async updateAllMandatoryStatuses(userId: string): Promise<void> {
    try {
      logDebug("Updating mandatory statuses for all pending tests", {
        userId: userId.substring(0, 8) + "***",
      });

      const tests = await this.getAllPendingPostTests(userId);

      for (const test of tests) {
        if (test.pendingPostTestId) {
          await this.updateMandatoryStatus(test.pendingPostTestId);
        }
      }

      logSuccess("All mandatory statuses updated", {
        userId: userId.substring(0, 8) + "***",
        testsUpdated: tests.length,
      });
    } catch (error) {
      logError("Error updating all mandatory statuses", error);
    }
  }

  /**
   * Check if user has any mandatory (deadline-passed) pending tests
   * Automatically updates mandatory statuses before checking
   */
  async hasMandatoryPendingPostTest(userId: string): Promise<boolean> {
    try {
      // First update all mandatory statuses
      await this.updateAllMandatoryStatuses(userId);

      // Then get updated tests
      const tests = await this.getAllPendingPostTests(userId);
      return tests.some((test) => test.isMandatory === true);
    } catch (error) {
      logError("Error checking mandatory pending post-tests", error);
      return false;
    }
  }

  /**
   * Clear pending post-test after completion (by sessionId)
   */
  async clearPendingPostTest(sessionId: string): Promise<void> {
    try {
      logInfo("Clearing pending post-test", { sessionId });

      const docRef = doc(this.pendingPostTestsCollection, sessionId);
      await deleteDoc(docRef);

      logSuccess("Pending post-test cleared", { sessionId });
    } catch (error) {
      logError("Error clearing pending post-test", error);
      throw error;
    }
  }

  /**
   * Clear all pending post-tests for a user
   */
  async clearAllPendingPostTests(userId: string): Promise<void> {
    try {
      const tests = await this.getAllPendingPostTests(userId);
      const deletePromises = tests.map((test) =>
        test.pendingPostTestId
          ? this.clearPendingPostTest(test.pendingPostTestId)
          : Promise.resolve()
      );
      await Promise.all(deletePromises);
      logSuccess("All pending post-tests cleared", { userId: userId.substring(0, 8) + "***" });
    } catch (error) {
      logError("Error clearing all pending post-tests", error);
      throw error;
    }
  }

  /**
   * Clear pending post-test for current user's specific session
   */
  async clearCurrentUserPendingPostTest(sessionId: string): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        logError("No authenticated user found");
        return;
      }

      await this.clearPendingPostTest(sessionId);
    } catch (error) {
      logError("Error clearing current user pending post-test", error);
      throw error;
    }
  }

  /**
   * Check if user should see reminder modal (once per login session)
   * The reminder modal should only appear ONCE per login session
   * After that, only the banner should show on specific pages
   */
  shouldShowReminder(pendingPostTest: PendingPostTest): boolean {
    // ✅ NEW LOGIC: Reminder modal appears once per login session
    // Session storage tracks if modal was shown during current login
    // This is checked in the component itself, so this method now just validates
    // that the test is valid and ready to be shown

    // Always allow showing if no lastReminderAt exists (first time)
    if (!pendingPostTest.lastReminderAt) {
      return true;
    }

    // Allow showing again after 6 hours (for users who stay logged in for long periods)
    const lastReminderTime = pendingPostTest.lastReminderAt.toMillis();
    const now = Date.now();
    const hoursSinceLastReminder = (now - lastReminderTime) / (1000 * 60 * 60);

    return hoursSinceLastReminder >= 6;
  }

  /**
   * Format session date for display
   */
  formatSessionDate(timestamp: any): string {
    try {
      if (timestamp?.toDate) {
        return timestamp.toDate().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return new Date(timestamp).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Recent session";
    }
  }

  /**
   * Detect and re-create pending post-tests for completed sessions that are missing them
   * This handles cases where post-test records are manually deleted from Firebase
   *
   * Logic:
   * 1. Get all completed sessions for the user
   * 2. Check if each session has a post_tests record
   * 3. Check if each session has a pending_post_tests record
   * 4. If session is completed but has NO post_tests AND NO pending_post_tests, re-create pending record
   *
   * Should be called:
   * - On user login
   * - Periodically (e.g., every 5 minutes via composable)
   * - After manual Firebase operations (for testing)
   */
  async detectAndRecreateMissingPendingPostTests(userId: string): Promise<number> {
    try {
      logInfo("Detecting missing pending post-tests for user", {
        userId: userId.substring(0, 8) + "***",
      });

      // Get all completed sessions for this user
      const sessionsCollection = collection(db, "sessions");
      const completedSessionsQuery = query(
        sessionsCollection,
        where("studentId", "==", userId),
        where("sessionStatus", "==", "completed")
      );
      const completedSessionsSnapshot = await getDocs(completedSessionsQuery);

      logDebug(`Found ${completedSessionsSnapshot.size} completed sessions`);

      // Get all post-tests for this user
      const postTestsCollection = collection(db, "post_tests");
      const postTestsQuery = query(postTestsCollection, where("user_id", "==", userId));
      const postTestsSnapshot = await getDocs(postTestsQuery);

      // Create a Set of session IDs that have post-tests
      const sessionIdsWithPostTests = new Set<string>();
      postTestsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.session_id) {
          sessionIdsWithPostTests.add(data.session_id);
        }
      });

      logDebug(`Found ${sessionIdsWithPostTests.size} sessions with post-tests`);

      // Get all pending post-tests for this user
      const pendingPostTestsSnapshot = await getDocs(
        query(this.pendingPostTestsCollection, where("userId", "==", userId))
      );

      // Create a Set of session IDs that have pending post-tests
      const sessionIdsWithPendingTests = new Set<string>();
      pendingPostTestsSnapshot.forEach((doc) => {
        sessionIdsWithPendingTests.add(doc.id); // doc.id is the sessionId
      });

      logDebug(`Found ${sessionIdsWithPendingTests.size} sessions with pending post-tests`);

      // Find completed sessions that are missing BOTH post-tests AND pending post-tests
      const missingSessions: Array<{
        sessionId: string;
        sessionData: any;
      }> = [];

      completedSessionsSnapshot.forEach((doc) => {
        const sessionId = doc.id;
        const sessionData = doc.data();

        // ✅ FIX: Exclude cancelled or rescheduled sessions - they should never trigger post-tests
        if (
          sessionData.sessionStatus === "cancelled" ||
          sessionData.cancelledAt ||
          sessionData.rescheduleReason
        ) {
          logDebug("Skipping cancelled/rescheduled session from post-test creation", {
            sessionId,
            status: sessionData.sessionStatus,
            reason: sessionData.cancellationReason || sessionData.rescheduleReason,
          });
          return; // Skip this session
        }

        const hasPostTest = sessionIdsWithPostTests.has(sessionId);
        const hasPendingTest = sessionIdsWithPendingTests.has(sessionId);

        // ✅ CRITICAL FIX: ONLY recreate if BOTH post-test AND pending test are missing
        // If post-test exists (student completed it), NEVER recreate pending test
        // This prevents duplicate pending tests after students complete their post-tests
        if (!hasPostTest && !hasPendingTest) {
          missingSessions.push({ sessionId, sessionData });
        } else if (hasPostTest && !hasPendingTest) {
          logDebug("Session already has completed post-test, not recreating pending test", {
            sessionId,
            subject: sessionData.subject,
          });
        } else if (!hasPostTest && hasPendingTest) {
          logDebug("Session has pending post-test waiting for student", {
            sessionId,
            subject: sessionData.subject,
          });
        }
      });

      logInfo(`Found ${missingSessions.length} sessions missing post-tests`, {
        userId: userId.substring(0, 8) + "***",
      });

      // Re-create pending post-tests for missing sessions
      let recreatedCount = 0;
      for (const { sessionId, sessionData } of missingSessions) {
        try {
          // ✅ CRITICAL FIX: Check if pending_post_test already exists
          // This prevents overwriting existing records with incorrect data
          const existingPendingTestDoc = await getDoc(doc(db, "pending_post_tests", sessionId));

          if (existingPendingTestDoc.exists()) {
            logDebug("Pending post-test already exists, skipping recreation", {
              sessionId,
              existingUserName: existingPendingTestDoc.data()?.userName,
            });
            continue; // Skip this session, don't overwrite existing record
          }

          // Get user info with robust error handling
          const userDoc = await getDoc(doc(db, "users", userId));
          if (!userDoc.exists()) {
            logError("User document not found when recreating pending post-test", {
              userId: userId.substring(0, 8) + "***",
              sessionId,
            });
            continue; // Skip this session if user not found
          }

          const userData = userDoc.data();
          const userName =
            userData.firstName && userData.lastName
              ? `${userData.firstName} ${userData.lastName}`
              : null; // ✅ FIX: Use null instead of "Student" to detect missing data

          if (!userName) {
            logError("User has incomplete name data, skipping pending post-test recreation", {
              userId: userId.substring(0, 8) + "***",
              sessionId,
              hasFirstName: !!userData.firstName,
              hasLastName: !!userData.lastName,
            });
            continue; // Skip this session if user name is incomplete
          }

          // Get tutor info
          const tutorId = sessionData.tutorId || "";
          let tutorName = "Tutor";
          if (tutorId) {
            const tutorDoc = await getDoc(doc(db, "users", tutorId));
            const tutorData = tutorDoc.exists() ? tutorDoc.data() : {};
            tutorName =
              tutorData.firstName && tutorData.lastName
                ? `${tutorData.firstName} ${tutorData.lastName}`
                : "Tutor";
          }

          // Use session start time (createPendingPostTest expects start time, not end time)
          const sessionStartTime = sessionData.startAt;

          // Re-create the pending post-test
          await this.createPendingPostTest(
            userId,
            userName,
            sessionId,
            sessionData.subject || "General",
            sessionStartTime,
            tutorId,
            tutorName
          );

          recreatedCount++;
          logSuccess("Re-created pending post-test for orphaned session", {
            sessionId,
            subject: sessionData.subject,
            userName,
          });
        } catch (error) {
          logError("Error re-creating pending post-test", {
            sessionId,
            error,
          });
        }
      }

      if (recreatedCount > 0) {
        logSuccess(`Re-created ${recreatedCount} missing pending post-tests`, {
          userId: userId.substring(0, 8) + "***",
        });
      }

      return recreatedCount;
    } catch (error) {
      logError("Error detecting missing pending post-tests", error);
      return 0;
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let pendingPostTestServiceInstance: PendingPostTestService | null = null;

export function getPendingPostTestService(): PendingPostTestService {
  if (!pendingPostTestServiceInstance) {
    pendingPostTestServiceInstance = new PendingPostTestService();
  }
  return pendingPostTestServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const pendingPostTestService = new Proxy({} as PendingPostTestService, {
  get(target, prop) {
    return getPendingPostTestService()[prop as keyof PendingPostTestService];
  },
});
export default PendingPostTestService;
